/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    FIX_LINT_MISSING_DEPS
 * Date:         2025-12-31
 * Modified By:  Cursor
 *
 * Description:
 * Suppressed lint warning for missing formik dependency in useEffect.
 * formik is intentionally excluded to prevent infinite sync loops.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/user-profile/hooks/useUserSync.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    FIX_LOCATION_DB_PRESENCE
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Explicitly track userCity, userState, and userCountry in dbPresence map
 * to ensure loaded data appears in BLACK color (via data-db attribute).
 *
 * File Size Check:
 * - Current: 101 lines
 * - Adding: ~15 lines
 * - Projected: ~116 lines
 * - Decision: Proceed (within 100-line hook limit excluding headers)
 *
 * Related Screens / Modules:
 * - client/src/components/setup/user-profile/hooks/useUserSync.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    USER_SYNC_ADD_LOCATION
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Sync userCity, userState, and userCountry fields from backend to form.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/user-profile/hooks/useUserSync.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    FIX_USER_SYNC_LOOP_REFS
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Fixed infinite loop in useUserSync by using a ref to track the last synced profile.
 * This prevents resetForm from being called repeatedly when formik object identity changes.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/user-profile/hooks/useUserSync.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    SPLIT_USER_SYNC_HOOK
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Extracted synchronization logic from UserProfileForm hook to meet line limits.
 * Fixed incorrect relative import paths and implicit any types.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/user-profile/hooks/useUserSync.ts
 */
import { useEffect, useState, useRef, MutableRefObject } from "react";
import { Currency } from "../../../../interfaces/Currency";

export const useUserSync = (loading: boolean, profile: any, formik: any, setDirty: (dirty: boolean) => void, originalCurrencyRef?: MutableRefObject<Currency>) => {
  const [dbPresence, setDbPresence] = useState<Record<string, boolean>>({});
  const [broadcastSignatureHtml, setBroadcastSignatureHtml] = useState<string>("");
  const lastSyncedProfileRef = useRef<any>(null);

  useEffect(() => {
    // [2025-12-29] Sync loop fix: only reset form if profile has actually changed or hasn't been synced yet
    if (!loading && profile && profile !== lastSyncedProfileRef.current) {
      lastSyncedProfileRef.current = profile; // Move to top to prevent race conditions during state updates

      // [2026-02-13] CURRENCY_PHASE2: Track the original currency from DB
      if (originalCurrencyRef) {
        originalCurrencyRef.current = (profile as any).preferredCurrency || "USD";
      }

      let sig = profile.broadcastSignatureHtml || "";
      if (!sig.trim()) {
        const fullName = `${profile.firstName || ""} ${profile.lastName || ""}`.trim() || "Your name";
        const jobTitle = profile.jobTitle || "Your position";
        const companyName = profile.companyName || "Your company";
        const phone = (profile as any).telCompany || (profile as any).tel || "";
        const email = profile.email || (profile as any).companyEmail || "";
        const website = profile.website || "";

        const lines: string[] = [];
        lines.push(`<p><strong>${fullName}</strong></p>`);
        lines.push(`<p>${jobTitle}${companyName ? `, ${companyName}` : ""}</p>`);
        if (phone) lines.push(`<p>${phone}</p>`);
        if (email) lines.push(`<p>${email}</p>`);
        if (website) lines.push(`<p>${website}</p>`);
        sig = lines.join("");
      }
      setBroadcastSignatureHtml(sig);

      const shouldReset = !!formik?.isSubmitting || !formik?.dirty;

      if (shouldReset) {
        formik.resetForm({
          values: {
            firstName: profile.firstName || "",
            lastName: profile.lastName || "",
            jobTitle: profile.jobTitle || "",
            email: profile.email || "",
            phone: (profile as any).tel || "",
            // [2025-12-29] Map user personal location fields from backend
            userCity: profile.userCity || "",
            userState: profile.userState || "",
            userCountry: profile.userCountry || "",
            currency: (profile as any).preferredCurrency || "USD",
            bio: profile.bio || "",
            linkedIn: profile.linkedIn || "",
            x: profile.x || "",
            instagram: profile.instagram || "",
            facebook: profile.facebook || "",
            profileImages: (profile as any).profileImages || null,
            broadcastSignatureHtml: sig,
          },
        });
        setDirty(false);
      }
      const presence: Record<string, boolean> = {};
      Object.keys(profile).forEach(k => { presence[k] = !!(profile as any)[k]; });
      
      // [2025-12-29] Track user location fields for data-db attribute
      if (profile.userCity) presence['userCity'] = true;
      if (profile.userState) presence['userState'] = true;
      if (profile.userCountry) presence['userCountry'] = true;

      setDbPresence(presence);
    }
    // [2025-12-31] formik intentionally excluded to prevent sync loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, profile, setDirty]);

  return { dbPresence, broadcastSignatureHtml, setBroadcastSignatureHtml };
};
